import { useEffect, useRef, useState } from 'react';
import { useOrganization, useUser } from '@clerk/react';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import {
  googlePickError,
  useGooglePicker,
} from '@/hooks/use-google-picker.hook';
import { useLazyGetSheetTriggerColumnsQuery } from '@/store/features/sheet-source.feature';
import {
  pickedSheetDraft,
  type SheetColumnsResponse,
  type SheetDraft,
} from './sheet-trigger.util';

/** A picked tab can only update its original current member and editor. */
export function useSheetSelection<T extends SheetDraft>({
  config,
  onChange,
  caller,
  enabled: callerEnabled,
  readOnly = false,
}: {
  config: T;
  onChange: (next: T) => void;
  caller: string;
  enabled: boolean;
  readOnly?: boolean;
}) {
  const { organization, isLoaded } = useOrganization();
  const { user, isLoaded: userLoaded } = useUser();
  const { unlocked } = useAgentsAccess();
  const picker = useGooglePicker();
  const enabled =
    callerEnabled &&
    unlocked &&
    !!isLoaded &&
    !!userLoaded &&
    !!organization &&
    !!user;
  const [readColumns] = useLazyGetSheetTriggerColumnsQuery();
  const [columns, setColumns] = useState<{
    scope: string;
    value: SheetColumnsResponse;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scope = JSON.stringify([
    enabled,
    organization?.id,
    user?.id,
    caller,
    readOnly,
    picker.canPick,
    config,
  ]);
  const metadataScope = JSON.stringify([
    enabled,
    organization?.id,
    user?.id,
    picker.canPick,
    config.sheet?.spreadsheetId,
    config.sheet?.sheetId,
  ]);
  const current = useRef({ scope, config, onChange, revision: 0, busy });
  const revision =
    current.current.revision + (current.current.scope === scope ? 0 : 1);
  current.current = { scope, config, onChange, revision, busy };
  const mounted = useRef(true);
  const operation = useRef(0);
  const request = useRef<ReturnType<typeof readColumns> | null>(null);
  useEffect(() => {
    operation.current += 1;
    request.current?.abort();
    setBusy(false);
    setError(null);
  }, [scope]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current += 1;
      request.current?.abort();
    };
  }, []);
  const commitEdit = (next: T) => {
    if (
      !mounted.current ||
      !enabled ||
      readOnly ||
      current.current.busy ||
      current.current.scope !== scope ||
      current.current.revision !== revision
    )
      return;
    current.current.onChange(next);
  };
  /**
   * Read a tab's real columns and keep them. With `tabName` (a pasted sheet
   * being picked), the tab is the one of that name, else `gid`: a link
   * copied from Share has no `#gid=` and saved tab 0. With `savedTab` (Change
   * sheet re-picking the saved spreadsheet), `gid` is the saved tab while it
   * still exists, else the first tab, so a deleted tab never blocks a re-pick.
   */
  const load = async (
    spreadsheetId: string,
    gid?: number | null,
    tabName?: string,
    savedTab = false
  ) => {
    if (!enabled || readOnly || !picker.canPick || !organization || !user)
      return null;
    const captured = scope,
      id = ++operation.current;
    const fresh = () =>
      mounted.current &&
      operation.current === id &&
      current.current.scope === captured;
    setBusy(true);
    setError(null);
    try {
      const read = (tab?: number | null) => {
        request.current = readColumns({
          organizationId: organization.id,
          spreadsheetId,
          gid: tab,
        });
        return request.current.unwrap();
      };
      const lookup = Boolean(tabName) || savedTab;
      let data = await read(lookup ? undefined : gid);
      if (!fresh()) return null;
      let expected = gid;
      if (lookup) {
        const tab =
          (tabName
            ? data.tabs.find((t) => t.sheetName === tabName)
            : undefined) ?? data.tabs.find((t) => t.sheetId === gid);
        expected = tab?.sheetId ?? null;
        if (tab && tab.sheetId !== data.sheetId) {
          data = await read(tab.sheetId);
          if (!fresh()) return null;
        }
      }
      if (
        data.spreadsheetId !== spreadsheetId ||
        data.googleUserId !== user.id ||
        (expected != null && data.sheetId !== expected)
      ) {
        setError('The selected sheet changed. Pick it again.');
        return null;
      }
      const next = pickedSheetDraft(current.current.config, data);
      const nextScope = JSON.stringify([
        enabled,
        organization.id,
        user.id,
        picker.canPick,
        data.spreadsheetId,
        data.sheetId,
      ]);
      setColumns({ scope: nextScope, value: data });
      current.current.onChange(next);
      return data;
    } catch (err) {
      if (fresh()) setError(googlePickError(err));
      return null;
    } finally {
      if (fresh()) setBusy(false);
    }
  };
  const choose = async (
    spreadsheetId?: string,
    gid?: number | null,
    tabName?: string
  ) => {
    if (!enabled || readOnly || busy || !picker.canPick) return null;
    const captured = scope,
      id = ++operation.current;
    const fresh = () =>
      mounted.current &&
      operation.current === id &&
      current.current.scope === captured;
    setBusy(true);
    setError(null);
    try {
      const picked = await picker.pick(spreadsheetId);
      if (!fresh() || !picked) return null;
      const saved = current.current.config.sheet;
      const same = picked.id === (spreadsheetId ?? saved?.spreadsheetId);
      if (!same) return await load(picked.id);
      // Change sheet passes no tab: fall back to the saved one, which may
      // have been deleted since it was saved.
      return gid == null && !tabName
        ? await load(picked.id, saved?.sheetId, undefined, true)
        : await load(picked.id, gid, tabName);
    } catch (err) {
      if (fresh()) setError(googlePickError(err));
      return null;
    } finally {
      if (fresh()) setBusy(false);
    }
  };
  return {
    enabled,
    busy,
    error,
    load,
    choose,
    commitEdit,
    canPick: picker.canPick,
    accountLabel: picker.accountLabel,
    metadata: columns?.scope === metadataScope ? columns.value : null,
  };
}
